/**
 * Pre-flight tier-access check used by composer send handlers.
 * See docs/pricing/line-tier-integration-plan.md §7d + §8 (edge cases).
 *
 * Returns a reason string when the send should be BLOCKED, or `null` when
 * it may proceed. The reason is UI-facing copy suitable for a toast/chip.
 * Server-side enforcement lives in quotaGuardService (Phase F) — this is
 * the client-side gate that keeps a locked tier from ever hitting the wire.
 */

import { estimateAiCreditsForText, type AiBillingModel } from './aiBilling'
import { AI_TIER_CATALOG, lockedTierCopy } from './aiTierCatalog'
import type { AiTierId } from '../types/ai'

export type AiTierAccessDenialCode =
  | 'tier_locked'
  | 'insufficient_credits'
  | 'ok'

export interface AiTierAccessCheckResult {
  code: AiTierAccessDenialCode
  reason: string | null
  /** Estimated credits for this call at the requested tier. */
  estimatedCredits: number
}

export interface AiTierAccessCheckInput {
  tier: AiTierId
  availableTiers: readonly AiTierId[]
  prompt: string
  /** User-visible plan name for locked-tier upsell copy. */
  planName?: string
  /** Current credit balance for this workspace/user. Omit to skip credit check. */
  availableCredits?: number
  /** Override the model used for cost estimation (defaults to tier peakModel). */
  billingModel?: AiBillingModel
}

export const evaluateTierAccess = ({
  tier,
  availableTiers,
  prompt,
  planName,
  availableCredits,
  billingModel,
}: AiTierAccessCheckInput): AiTierAccessCheckResult => {
  const model = billingModel ?? AI_TIER_CATALOG[tier].peakModel
  const quote = estimateAiCreditsForText(model, prompt)
  const estimatedCredits = quote.credits

  if (!availableTiers.includes(tier)) {
    return {
      code: 'tier_locked',
      reason: lockedTierCopy(tier, planName ?? ''),
      estimatedCredits,
    }
  }

  if (typeof availableCredits === 'number' && estimatedCredits > availableCredits) {
    return {
      code: 'insufficient_credits',
      reason: `${AI_TIER_CATALOG[tier].label} needs about ${estimatedCredits} credits — you have ${Math.max(0, Math.floor(availableCredits))}.`,
      estimatedCredits,
    }
  }

  return { code: 'ok', reason: null, estimatedCredits }
}
